import dedent from 'ts-dedent'
import {
  ColumnDataType,
  GetOutputSchemaParams,
  Provider,
  TableSchema,
  WorkflowComponentCodeParams,
  WorkflowComponentParamType
} from '../types'
import { addColumn } from '../utils/schemas'
import { SelectWorkflowComponent } from './common/selectcomponent'

class NormalizeColumn extends SelectWorkflowComponent {
  constructor() {
    super(
      {
        name: 'native.normalize',
        group: 'Data Preparation',
        title: 'Normalize',
        description: `
      This component normalizes the values of a given column.

      Normalization can be computed as 0-1 values or as z-scores.
      `
      },

      [
        {
          name: 'source',
          type: WorkflowComponentParamType.InputTable,
          title: 'Source table'
        },
        {
          name: 'column',
          type: WorkflowComponentParamType.Column,
          title: 'Column to normalize',
          dataType: [ColumnDataType.Number],
          parent: 'source'
        },
        {
          name: 'zscores',
          type: WorkflowComponentParamType.Boolean,
          title: 'Use z-scores',
          default: false
        }
      ]
    )
  }

  getInnerSql({ params, connection }: WorkflowComponentCodeParams) {
    if (connection.type === Provider.DatabricksRest) {
      if (params.zscores) {
        return dedent`SELECT /*+ REPARTITION(40) */
              *,
              (${params.column} - AVG(${params.column}) OVER ()) /
              (STDDEV(${params.column}) OVER () )
              AS ${params.column}_norm
          FROM ${connection.quoteName(params.source as string)}`
      } else {
        return dedent`SELECT /*+ REPARTITION(40) */
              *,
              (${params.column} - MIN(${params.column}) OVER ()) /
              (MAX(${params.column}) OVER () - MIN(${params.column}) OVER ())
              AS ${params.column}_norm
          FROM ${connection.quoteName(params.source as string)}`
      }
    } else if (connection.type === Provider.Oracle) {
      if (params.zscores) {
        return dedent`WITH stats AS (
              SELECT *,
                AVG(${params.column}) OVER () as col_avg,
                STDDEV(${params.column}) OVER () as col_stddev
              FROM ${connection.quoteName(params.source as string)}
            )
            SELECT *,
              (${params.column} - col_avg) / col_stddev AS ${params.column}_norm
            FROM stats`
      } else {
        return dedent`WITH stats AS (
              SELECT *,
                MIN(${params.column}) OVER () as col_min,
                MAX(${params.column}) OVER () as col_max
              FROM ${connection.quoteName(params.source as string)}
            )
            SELECT *,
              CASE 
                WHEN (col_max - col_min) = 0 THEN 0
                ELSE (${params.column} - col_min) / (col_max - col_min)
              END AS ${params.column}_norm
            FROM stats`
      }
    } else {
      if (params.zscores) {
        return dedent`SELECT *,
              (${params.column} - AVG(${params.column}) OVER ()) /
              (STDDEV(${params.column}) OVER () )
              AS ${params.column}_norm
          FROM ${connection.quoteName(params.source as string)}`
      } else {
        return dedent`SELECT *,
              (${params.column} - MIN(${params.column}) OVER ()) /
              (MAX(${params.column}) OVER () - MIN(${params.column}) OVER ())
              AS ${params.column}_norm
          FROM ${connection.quoteName(params.source as string)}`
      }
    }
  }

  canProvideOutputSchemas(): boolean {
    return true
  }

  getOutputSchema({ columns, params }: GetOutputSchemaParams): TableSchema {
    return addColumn(columns.source, {
      name: `${params.column}_norm`,
      type: ColumnDataType.Number
    })
  }
  supportedProviders(): Provider[] {
    return [
      Provider.BigQuery,
      Provider.Postgres,
      Provider.Redshift,
      Provider.Snowflake,
      Provider.DatabricksRest,
      Provider.Oracle
    ]
  }
}

export { NormalizeColumn }
